import { type RefObject, useState } from 'react'

import { createPortal } from 'react-dom'
import { useIsomorphicLayoutEffect } from 'react-use'

import { useActionPanel } from '../ActionPanelProvider'
import { ActionPanelToggle } from '../ActionPanelToggle'
import { Overlay } from './ui'

/**
 * The way back from `minimized`, and the only one.
 *
 * `minimized` hides the action column outright rather than narrowing it, because
 * a column has one width for its header and its body — so any width that left
 * room for a header control would leave that much dead space in every row, and
 * the design shows the rows with no action cell at all. Hiding the column takes
 * the header's chevron with it, so the control has to live outside the column
 * model.
 *
 * It lives in the grid's own root element, through a portal. The portal is not
 * a detail: as a DOM child of the grid the control inherits AG Grid's theme
 * variables, so its height, its fill, its border and its clearance from the
 * scrollbar gutter are all AG Grid's own values. Positioned beside the grid
 * instead, every one of those had to be republished or measured, and the control
 * still drifted from the header control it replaces. React context reaches it
 * either way, because a portal moves the DOM node and not the tree.
 */
export const MinimizedPanelOverlay = ({
  containerRef,
  isGridReady,
}: {
  containerRef: RefObject<HTMLDivElement | null>
  /** The grid's DOM only exists once it is ready; this is when to go looking. */
  isGridReady: boolean
}) => {
  const { setIsExpanded, variant } = useActionPanel()
  const [gridRoot, setGridRoot] = useState<HTMLElement | null>(null)

  // Resolving once is enough: AG Grid renders `.ag-root` through React at a
  // fixed position, so the node lives as long as the grid does.
  useIsomorphicLayoutEffect(() => {
    const element = containerRef.current?.querySelector('.ag-root')
    setGridRoot(element instanceof HTMLElement ? element : null)
  }, [isGridReady])

  if (variant !== 'minimized' || !gridRoot) return null

  return createPortal(
    <Overlay>
      <ActionPanelToggle isExpanded={false} setIsExpanded={setIsExpanded} />
    </Overlay>,
    gridRoot
  )
}
